import React, { useEffect, useState } from 'react';
import { Box, Paper, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppDispatch } from '../../stateManagement/store';
import { useDispatch } from 'react-redux';
import { ILoyaltyPointRequest } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { useSelector } from 'react-redux';
import { createLoyaltyPointAsync, updateLoyaltyPointAsync } from '../../stateManagement/loyaltyPointSlice';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import InfoComponent from '../../customComponents/InfoComponent';

export const loyaltyPointSchema = z.object({
    loyaltyPointId: z.string(),
    organizationId: z.string().optional(),
    startDate: z.string().min(1, "Start Date cannot be empty."),
    endDate: z.string().optional(),
    referralPoints: z.number().optional(),
    usedPointsCount: z.number().optional(),
    usedAmount: z.number().optional(),
    earnedPointsCount: z.number().optional(),
    earnedAmount: z.number().optional(),
});

interface LoyaltyPointFormProps {
    loyaltyPointInput?: ILoyaltyPointRequest | null;
    closeForm: () => void;
}

const LoyaltyPointForm: React.FC<LoyaltyPointFormProps> = ({ loyaltyPointInput, closeForm }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch<AppDispatch>();

    const organization = useSelector(selectSelectedOrganization);

    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<ILoyaltyPointRequest>({
        resolver: zodResolver(loyaltyPointSchema),
        defaultValues: {
            loyaltyPointId: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: undefined,
            referralPoints: 0,
            usedPointsCount: 0,
            usedAmount: 0,
            earnedPointsCount: 0,
            earnedAmount: 0,
        },
    });

    useEffect(() => {
        if (loyaltyPointInput) {
            if (loyaltyPointInput?.loyaltyPointId) {
                reset({
                    loyaltyPointId: loyaltyPointInput.loyaltyPointId,
                    startDate: loyaltyPointInput.startDate.split('T')[0],
                    endDate: loyaltyPointInput.endDate ? loyaltyPointInput.endDate.split('T')[0] : '',
                    referralPoints: loyaltyPointInput.referralPoints,
                    usedPointsCount: loyaltyPointInput.usedPointsCount,
                    usedAmount: loyaltyPointInput.usedAmount,
                    earnedPointsCount: loyaltyPointInput.earnedPointsCount,
                    earnedAmount: loyaltyPointInput.earnedAmount,
                });
            }
            else {
                reset({
                    loyaltyPointId: '',
                    startDate: new Date().toISOString().split('T')[0],
                    endDate: undefined,
                    referralPoints: 0,
                    usedPointsCount: 0,
                    usedAmount: 0,
                    earnedPointsCount: 0,
                    earnedAmount: 0,
                });
            }
        }
        setLoading(false);
    }, [loyaltyPointInput, reset]);

    const handleSave = async (data: ILoyaltyPointRequest) => {

        setLoading(true);
        try {
            if (!data.endDate || String(data.endDate).trim() === '') {
                data.endDate = undefined;
            }
            let result;
            if (loyaltyPointInput?.loyaltyPointId) {
                result = await dispatch(updateLoyaltyPointAsync({ organizationId: organization.organizationId ?? '', data })).unwrap();
            } else {
                result = await dispatch(createLoyaltyPointAsync({ organizationId: organization.organizationId ?? '', data })).unwrap();
            }
            if (result) {
                closeForm();
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }

    };

    return (
        <Box>
            <CustomDialogTitle handleClose={closeForm} title={loyaltyPointInput?.loyaltyPointId ? 'Edit Loyalty Rule' : 'New Loyalty Rule'} loading={loading} />
            <CustomDialogContent>
                <form onSubmit={handleSubmit(handleSave)} noValidate>
                    <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <TextField fullWidth label="Start Date" InputLabelProps={{ shrink: true }} type="date" {...register('startDate')} error={!!errors.startDate} helperText={errors.startDate?.message} />

                        <TextField fullWidth label="End Date" InputLabelProps={{ shrink: true }} type="date" {...register('endDate')} error={!!errors.endDate} helperText={errors.endDate?.message} />
                    </Box>

                    <Box mt={2}>
                        <InfoComponent infoContent='Points awarded when a referral is made' >
                            <TextField
                                fullWidth
                                type="number"
                                label="Referral points"
                                InputLabelProps={{ shrink: true }}
                                {...register('referralPoints', { valueAsNumber: true })}
                                error={!!errors.referralPoints}
                                helperText={errors.referralPoints?.message}
                                inputProps={{ step: "0.01", min: 0 }}
                            />
                        </InfoComponent>
                    </Box>

                    <Paper sx={{ p: '20px', marginTop: '10px' }}>
                        <Box mt={2}>
                            <InfoComponent infoContent='The number of points a client must use to receive the value defined below' >
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Points required to redeem"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('usedPointsCount', { valueAsNumber: true })}
                                    error={!!errors.usedPointsCount}
                                    helperText={errors.usedPointsCount?.message}
                                    inputProps={{ step: "0.01", min: 0 }}
                                />
                            </InfoComponent>
                        </Box>

                        <Box mt={2}>
                            <InfoComponent infoContent='The monetary value the client receives when redeeming the points above' >
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Value per redemption"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('usedAmount', { valueAsNumber: true })}
                                    error={!!errors.usedAmount}
                                    helperText={errors.usedAmount?.message}
                                    inputProps={{ step: "0.01", min: 0 }}
                                />
                            </InfoComponent>
                        </Box>
                    </Paper>

                    <Paper sx={{ p: '20px', marginTop: '10px' }}>
                        <Box mt={2}>
                            <InfoComponent infoContent='The number of points a client earns for spending the amount defined below' >
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Points earned"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('earnedPointsCount', { valueAsNumber: true })}
                                    error={!!errors.earnedPointsCount}
                                    helperText={errors.earnedPointsCount?.message}
                                    inputProps={{ step: "0.01", min: 0 }}
                                />
                            </InfoComponent>
                        </Box>

                        <Box mt={2}>
                            <InfoComponent infoContent='The monetary amount a client must spend to earn the points above' >
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Amount spent to earn"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('earnedAmount', { valueAsNumber: true })}
                                    error={!!errors.earnedAmount}
                                    helperText={errors.earnedAmount?.message}
                                    inputProps={{ step: "0.01", min: 0 }}
                                />
                            </InfoComponent>
                        </Box>
                    </Paper>

                    <CustomFormAction>
                        <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the ${loyaltyPointInput?.loyaltyPointId ? 'updated' : 'new'} loyalty point record`} />
                    </CustomFormAction>

                </form>
            </CustomDialogContent>
        </Box>
    );
};

export default LoyaltyPointForm;